import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { IonContent, IonLoading, IonPage, useIonAlert, useIonViewDidEnter, useIonViewWillEnter } from '@ionic/react';
import {
  AppHeader,
  ItemCard,
  LabelArrowRedirect,
  Container,
  LetterAvatar,
  IconDelete,
  IconLock,
  IconArrowLeft,
  IconStylus,
  IconButton,
} from '@mindly/ui-components';

// utils
import { userSelector } from '../../redux/user/selector';
import { setUser } from '../../redux/user/reducer';
import { setActiveTab, setIsActive, Tabs } from '../../redux/tabBar/reducer';
// styles
import { Avatar, StyledImageInfoWrapper } from './Profile.style';

// types
import UserTypeToServerUserTypeAdapter, { ServerUserType } from './UserTypeToServerUserTypeAdapter';
import BuildInformation from '../../components/BuildInformation/BuildInformation';
import { PrivateRoutes, PublicRoutes } from 'src/routes/navigation';
import { usersPsychologistsSelector } from '../../redux/usersPsychologists/selector';
import { getUsersPsychologistsListThunk } from '../../redux/usersPsychologists/action';
import AddAvatarModal from '../../modals/AddAvatarModal';
import { useIntl } from 'react-intl';
import { setSavedCreditCards } from '../../redux/savedCreditCards/reducer';
import { setUsersPsychologists } from '../../redux/usersPsychologists/reducer';
import { resetConsultations } from '../../redux/consultations/reducer';
import { resetSubscriptions } from '../../redux/subscriptions/reducer';
import { resetSubscriptionsInfo } from '../../redux/subscriptionsInfo/reducer';
import { resetRooms } from '../../redux/chatRooms/reducer';
import { resetChatMessages } from '../../redux/chatMessages/reducer';
import useToast from '../../context/ToastContext/ToastContext';
import { resetSpecialist } from '../../redux/specialists/reducer';
import { resetFavouriteSpecialists } from '../../redux/favouriteSpecialits/reducer';
import { useAnalytics } from '../../shared';
import { getIsExistsSpecialist } from '../../api/user';
import { MindlyStreamChatCapacitor } from '@mindly/stream-chat-capacitor';
import { useAuthFlow } from 'src/shared/hooks/useAuthFlow';
import { isUserHaveUnitedStatesCountry } from 'src/shared/utils/isUserHaveUnitedStatesCountry';

const Profile: React.FC = () => {
  const { t } = useTranslation('profile');
  const { t: appT } = useTranslation('app');
  const history = useHistory();
  const user = useSelector(userSelector);
  const [present] = useIonAlert();
  const intl = useIntl();
  const { showToast } = useToast();
  const { deleteUser, triggerSignOutFlow } = useAuthFlow();

  const specialistExistsLoading = useRef(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showLoader, setIsShowLoader] = useState(false);
  const dispatch = useDispatch();
  const [showChangeAvatarModal, setShowChangeAvatarModal] = useState(false);

  const usersPsychologistList = useSelector(usersPsychologistsSelector);
  const { trackPredefinedEvent } = useAnalytics();

  useIonViewDidEnter(() => {
    trackPredefinedEvent('PROFILE_USER_DATA_SCREEN_VIEWED');
  }, [trackPredefinedEvent]);

  useIonViewWillEnter(() => {
    if (!user?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }

    if (!usersPsychologistList && user?.id) {
      dispatch(getUsersPsychologistsListThunk());
    }
  }, [user?.id]);

  useEffect(() => {
    dispatch(setIsActive(true));
    dispatch(setActiveTab(Tabs.third));
  }, [dispatch]);

  const userFromServer: ServerUserType | null = useMemo<ServerUserType | null>(
    () => user && UserTypeToServerUserTypeAdapter(user),
    [user?.id]
  );

  const handleRevokeAccount = useCallback(async () => {
    setIsShowLoader(true);
    try {
      trackPredefinedEvent('PROFILE_DELETE_ACCOUNT_BUTTON_SUBMITTED');

      const deleteResult = await deleteUser();
      if (!deleteResult.data || deleteResult.error) {
        throw new Error(deleteResult?.error as unknown as string);
      }

      await triggerSignOutFlow();
      await MindlyStreamChatCapacitor.logoutChatUser();
      history.push(PublicRoutes.AUTH);

      dispatch(setSavedCreditCards(null));
      dispatch(setUsersPsychologists(null));
      dispatch(resetConsultations());
      dispatch(setUser(null));
      dispatch(resetSubscriptions());
      dispatch(resetSubscriptionsInfo());
      dispatch(resetRooms());
      dispatch(resetChatMessages());
      dispatch(resetSpecialist());
      dispatch(resetFavouriteSpecialists());
    } catch (error) {
      const message = (error as Error).message;
      console.warn('Remove account warn: ', message);
      showToast({
        message: t(message) !== message ? t(message) : appT(message) !== message ? appT(message) : message,
      });
    } finally {
      setIsShowLoader(false);
    }
  }, []);

  const showDeleteProfileModal = useCallback(() => {
    present({
      mode: 'ios',
      header: t('personal_details.delete_acc_confirmation.title'),
      subHeader: t('personal_details.delete_acc_confirmation.description'),
      buttons: [
        {
          text: t('personal_details.delete_acc_confirmation.delete_button'),
          cssClass: 'confirm-delete-button',
          handler: handleRevokeAccount,
        },
        {
          text: t('personal_details.delete_acc_confirmation.back_button'),
        },
      ],
    });
  }, [present, intl, handleRevokeAccount]);

  const showSpecialistExistsWarning = useCallback(() => {
    present({
      mode: 'ios',
      header: t('personal_details.delete_acc_confirmation_for_therapists.title'),
      subHeader: t('personal_details.delete_acc_confirmation_for_therapists.description'),
      buttons: [
        {
          text: t('personal_details.delete_acc_confirmation_for_therapists.button'),
        },
      ],
    });
  }, [present]);

  const runDeleteProfileProcess = useCallback(async () => {
    if (user?.id && !specialistExistsLoading.current) {
      trackPredefinedEvent('PROFILE_DELETE_ACCOUNT_BUTTON_CLICKED');
      specialistExistsLoading.current = true;
      getIsExistsSpecialist(user?.id).then(
        ({ data, error, message }) => {
          specialistExistsLoading.current = false;

          if (error) {
            console.warn('Error removing user', error);
            showToast({
              message: message
                ? t(message) !== message
                  ? t(message)
                  : appT(message) !== message
                    ? appT(message)
                    : message
                : 'Something went wrong',
            });
            return;
          }

          if (data) {
            showSpecialistExistsWarning();
          } else {
            showDeleteProfileModal();
          }
        },
        () => (specialistExistsLoading.current = false)
      );
    }
  }, [user?.id]);

  return (
    <IonPage>
      <AppHeader className="flex flex-row items-start mb-4 cursor-pointer">
        <IconButton icon={<IconArrowLeft />} onClick={() => history.back()} />
      </AppHeader>
      <IonContent>
        <Container>
          <StyledImageInfoWrapper>
            {!userFromServer?.avatar && (
              <LetterAvatar className="letter-avatar" letter={user?.display_name?.charAt(0) ?? '?'} />
            )}

            {!!userFromServer?.avatar && (
              <Avatar
                isLoading={isLoading}
                className={'image-Profile'}
                src={userFromServer?.avatar}
                onLoad={() => setIsLoading(false)}
              />
            )}
            <div
              onClick={() => setShowChangeAvatarModal(true)}
              className="rounded-full bg-color-white w-10 h-10 flex items-center justify-center cursor-pointer absolute bottom-0 right-[30%]"
            >
              <div className="flex items-center justify-center rounded-full bg-color-black w-9 h-9">
                <IconStylus color={'white'} />
              </div>
            </div>
          </StyledImageInfoWrapper>
          <ItemCard className="mt-6" variant="transparent20" size="M45">
            <LabelArrowRedirect
              className="flex justify-between w-full"
              label={t('personal_details.name.subtitle')}
              subLabel={(user?.display_name as string) || (user?.displayName as string)}
              routeClick={() => history.push(PrivateRoutes.PROFILE_NAME)}
            />
            <div className="my-4 border-b border-solid border-color-black border-opacity-10" />
            <LabelArrowRedirect
              className="flex justify-between w-full"
              label={t('personal_details.last_name.subtitle')}
              subLabel={user?.last_name}
              routeClick={() => history.push(PrivateRoutes.PROFILE_LAST_NAME)}
            />
            <div className="my-4 border-b border-solid border-color-black border-opacity-10" />
            <LabelArrowRedirect
              className="flex justify-between w-full"
              label={t('personal_details.date_of_birth.subtitle')}
              subLabel={user?.dob ?? '-'}
              routeClick={() => history.push(PrivateRoutes.PROFILE_DOB)}
            />
            <div className="my-4 border-b border-solid border-color-black border-opacity-10" />
            <LabelArrowRedirect
              className="flex justify-between w-full"
              label={t('country_of_living.title')}
              subLabel={
                isUserHaveUnitedStatesCountry(user?.country ?? '')
                  ? user?.us_state ?? user?.country ?? '-'
                  : user?.country ?? '-'
              }
              routeClick={() => history.push(PrivateRoutes.PROFILE_COUNTRY_OF_LIVING)}
            />
            <div className="my-4 border-b border-solid border-color-black border-opacity-10" />
            <LabelArrowRedirect
              className="flex justify-between w-full"
              label={t('personal_details.tax_id.subtitle')}
              subLabel={user?.tax_id ?? '-'}
              routeClick={() => history.push(PrivateRoutes.PROFILE_TAX_ID)}
            />
            <div className="my-4 border-b border-solid border-color-black border-opacity-10" />
            <LabelArrowRedirect
              className="flex justify-between w-full"
              label={t('personal_details.email.subtitle')}
              subLabel={user?.email}
              routeClick={() => history.push(PrivateRoutes.PROFILE_EMAIL)}
            />
          </ItemCard>
          <ItemCard className="mt-4" variant="transparent20" size="M45">
            <div className="flex flex-row items-start">
              <IconLock className="mr-4" />
              <LabelArrowRedirect
                className="flex justify-between w-full"
                subLabel={t('personal_details.change_passwod.title')}
                routeClick={() => history.push(PrivateRoutes.PROFILE_PASSWORD)}
              />
            </div>
          </ItemCard>
          <ItemCard className="mt-4 mb-6" variant="transparent20" size="M45">
            <div className="flex flex-row items-start">
              <IconDelete className="mr-4" />
              <LabelArrowRedirect
                className="flex justify-between w-full"
                subLabel={t('personal_details.delete_acc.title')}
                routeClick={runDeleteProfileProcess}
              />
            </div>
          </ItemCard>
          <BuildInformation />
        </Container>
        <IonLoading isOpen={showLoader} onDidDismiss={() => setIsShowLoader(false)} spinner={'circles'} />
      </IonContent>
      <AddAvatarModal
        isOpen={showChangeAvatarModal}
        onDidDismiss={() => setShowChangeAvatarModal(false)}
        defaultImage={user?.avatar}
      />
    </IonPage>
  );
};

export default Profile;
